import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";

const ConfirmationModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
  padding: 20px;
`;

const ConfirmationModalContainer = styled.div`
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 640px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  animation: modalSlideIn 0.3s ease-out;

  @keyframes modalSlideIn {
    from {
      opacity: 0;
      transform: scale(0.95) translateY(20px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }
`;

const ConfirmationModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #e0e0e0;
  background: #f8f9fa;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
`;

const ConfirmationModalTitle = styled.h3`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 18px;
  color: #414141;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  padding: 4px 8px;
  font-size: 20px;
  cursor: pointer;
  color: #7e7e7e;
  border-radius: 4px;
  transition: all 0.2s;

  &:hover {
    background: #f0f0f0;
    color: #414141;
  }
`;

const ConfirmationModalContent = styled.div`
  padding: 20px 24px;
`;

const FormGroup = styled.div`
  margin-top: 16px;
`;

const FormLabel = styled.label`
  display: block;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  color: #414141;
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-family: "Poppins";
  font-size: 13px;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #476dfa;
  }

  &::placeholder {
    color: #aaa;
  }
`;

const ConfirmationModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px 24px 24px;
  border-top: 1px solid #f0f0f0;
`;

const ConfirmationCancelButton = styled.button`
  padding: 10px 20px;
  background: transparent;
  border: 1px solid #e0e0e0;
  border-radius: 7px;
  color: #7e7e7e;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f8f9fa;
    border-color: #c0c0c0;
  }
`;

const ConfirmationDeleteButton = styled.button`
  padding: 10px 20px;
  background: ${(props) => (props.disabled ? "#ccc" : "#476dfa")};
  border: none;
  border-radius: 7px;
  color: white;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  transition: background-color 0.2s;

  &:hover:not(:disabled) {
    background: #3d5ce6;
  }
`;

const MultiSelect = styled.div`
  display: grid;
  gap: 8px;
`;

const SelectedChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border: 1px solid #e7e7e7;
  border-radius: 999px;
  background: #fafafa;
  font-size: 12px;
`;

const RemoveChip = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  color: #6b7280;
`;

const SearchRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 8px;
  position: relative;
`;

const OptionsList = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  border: 1px solid #e7e7e7;
  border-radius: 8px;
  max-height: 220px;
  overflow: auto;
  background: #ffffff;
  box-shadow: 0 8px 24px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.06);
  z-index: 30;
`;

const OptionItem = styled.label`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 10px;
  align-items: center;
  padding: 10px 12px;
  cursor: pointer;
  &:hover { background: #f8fafc; }
`;

const Small = styled.div`
  font-size: 12px;
  color: #6b7280;
`;

function getDisplayName(m) {
  if (!m) return "";
  const full = m.full_name && String(m.full_name).trim();
  if (full) return full;
  const fn = m.first_name || "";
  const ln = m.last_name || "";
  const name = `${fn} ${ln}`.trim();
  return name || m.email || `ID ${m.id}`;
}

export default function AddManagersModal({ isOpen, onClose, onSubmit, managers = [], loading }) {
  const [selectedIds, setSelectedIds] = useState([]);
  const [query, setQuery] = useState("");
  const inputRef = useRef(null);
  const [inputFocused, setInputFocused] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setSelectedIds([]);
      setQuery("");
    }
  }, [isOpen]);

  const normalized = useMemo(() => {
    const list = Array.isArray(managers) ? managers : [];
    const dedup = new Map();
    list.forEach((m) => {
      if (m && m.id && !dedup.has(m.id)) dedup.set(m.id, m);
    });
    const arr = Array.from(dedup.values());
    arr.sort((a, b) => getDisplayName(a).localeCompare(getDisplayName(b)));
    return arr;
  }, [managers]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return normalized;
    return normalized.filter((m) =>
      [getDisplayName(m), m.email || ""].some((t) => t && t.toLowerCase().includes(q))
    );
  }, [normalized, query]);

  const filteredNotSelected = useMemo(() => {
    if (!Array.isArray(filtered)) return [];
    if (selectedIds.length === 0) return filtered;
    const selectedSet = new Set(selectedIds);
    return filtered.filter((m) => !selectedSet.has(m.id));
  }, [filtered, selectedIds]);

  const addId = (id) => {
    if (!selectedIds.includes(id)) {
      setSelectedIds((prev) => [...prev, id]);
    }
    setQuery("");
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const removeId = (id) => setSelectedIds((prev) => prev.filter((x) => x !== id));
  const clearAll = () => setSelectedIds([]);

  if (!isOpen) return null;

  const modalContent = (
    <ConfirmationModalOverlay onClick={onClose}>
      <ConfirmationModalContainer onClick={(e) => e.stopPropagation()}>
        <ConfirmationModalHeader>
          <ConfirmationModalTitle>Add Managers</ConfirmationModalTitle>
          <CloseButton type="button" onClick={onClose}>
            ×
          </CloseButton>
        </ConfirmationModalHeader>

        <ConfirmationModalContent>
          <FormGroup>
            <FormLabel>Select managers</FormLabel>
            <MultiSelect>
              <SelectedChips>
                {selectedIds.map((id) => {
                  const m = normalized.find((x) => x.id === id);
                  return (
                    <Chip key={id}>
                      {getDisplayName(m)}
                      <RemoveChip type="button" onClick={() => removeId(id)}>×</RemoveChip>
                    </Chip>
                  );
                })}
                {selectedIds.length === 0 && <Small>No managers selected</Small>}
                {selectedIds.length > 0 && (
                  <RemoveChip type="button" onClick={clearAll} aria-label="Clear all selected">Clear all</RemoveChip>
                )}
              </SelectedChips>
              <SearchRow>
                <Input
                  placeholder="Search managers by name or email"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onFocus={() => setInputFocused(true)}
                  onBlur={() => setInputFocused(false)}
                  onKeyDown={(e) => {
                    if (e.key === 'Backspace' && query === '' && selectedIds.length > 0) {
                      setSelectedIds((prev) => prev.slice(0, -1));
                    } else if (e.key === 'Enter') {
                      if (query.trim().length > 0 && filteredNotSelected.length > 0) {
                        e.preventDefault();
                        addId(filteredNotSelected[0].id);
                      }
                    }
                  }}
                  ref={inputRef}
                />
                {inputFocused && query.trim().length > 0 && (
                  <OptionsList role="listbox" aria-label="Manager results">
                    {filteredNotSelected.map((m) => (
                      <OptionItem
                        key={m.id}
                        onMouseDown={(e) => { e.preventDefault(); addId(m.id); }}
                        role="option"
                        aria-selected={false}
                      >
                        <div style={{ width: 12, height: 12, border: '1px solid #e5e7eb', borderRadius: 3, background: '#fff' }} />
                        <div>
                          <div style={{ fontWeight: 600 }}>{getDisplayName(m)}</div>
                          <Small>{m.email}</Small>
                        </div>
                      </OptionItem>
                    ))}
                    {filteredNotSelected.length === 0 && (
                      <div style={{ padding: 10 }}><Small>No matches</Small></div>
                    )}
                  </OptionsList>
                )}
              </SearchRow>
              {query.trim().length === 0 && (
                <div style={{ padding: '2px 2px 0 2px' }}><Small>Type to search and add managers. Selected appear above.</Small></div>
              )}
            </MultiSelect>
          </FormGroup>
        </ConfirmationModalContent>

        <ConfirmationModalFooter>
          <ConfirmationCancelButton type="button" onClick={onClose}>Cancel</ConfirmationCancelButton>
          <ConfirmationDeleteButton
            type="button"
            onClick={() => onSubmit(selectedIds)}
            disabled={loading || selectedIds.length === 0}
          >
            Add
          </ConfirmationDeleteButton>
        </ConfirmationModalFooter>
      </ConfirmationModalContainer>
    </ConfirmationModalOverlay>
  );

  return createPortal(modalContent, document.body);
}
